import { useMemo } from 'react';

import {
   AppointmentEncounterMode,
   AppointmentNotUpdatableFault,
   AppointmentUpdateCommand,
} from '@dreryk/egabinet-appointment-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import {
   ActionEventInterceptor,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { AppointmentAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.appointment-advance-payment';

import { appointmentsTranslationKeys } from '../../../../translations';
import { parseToDateWithMidnightAdjustment } from '../../shared/form/AppointmentForm.utils';
import { AppointmentUpdateFormBean, AppointmentUpdateFormController } from '../shared/AppointmentUpdateForm';

type AppointmentUpdateActionEventInterceptorProps = Readonly<
   Pick<AppointmentUpdateFormController, 'handleSubmit'> & {
      children: JSX.Element;
      appointmentId: number;
   }
>;

export const AppointmentUpdateActionEventInterceptor = (
   props: AppointmentUpdateActionEventInterceptorProps
): JSX.Element => {
   const appointmentId = useMemo(() => AppointmentId.of(props.appointmentId), [props.appointmentId]);
   const { executeAsync: updateAppointment } = useCqrsCommand<
      void,
      AppointmentUpdateFormBean,
      AppointmentNotUpdatableFault
   >(
      (parameters) => {
         const command = new AppointmentUpdateCommand();
         command.location = parameters.healthcareProviderLocation!.locationIdentifier;
         command.appointmentId = appointmentId;
         command.patientId = parameters.patient!.id!;
         command.practitionerId = parameters.practitioner.id;
         command.note = Choice.withUseDefaultIfNull(parameters.note);
         command.timeSlot = TimeSlot.of(
            parseToDateWithMidnightAdjustment('START', parameters.date, parameters.startTime),
            parseToDateWithMidnightAdjustment('END', parameters.date, parameters.endTime)
         );

         if (parameters.videoConsultation) {
            command.encounterMode = Choice.of<AppointmentEncounterMode>('VIDEO_CONSULTATION');
         }

         if (parameters.receptionService?.value) {
            command.receptionServiceIds = parameters.receptionService.value.map(
               ({ receptionService: { receptionServiceId } }) => ReceptionServiceId.of(receptionServiceId)
            );
         }

         command.timeSlotConfirmed = parameters.dateConfirmedWithPatient;

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(appointmentsTranslationKeys.successfullyUpdatedAppointment);
         },
         onFault: {
            'egabinet-appointment-frontend-api:AppointmentNotUpdatableFault': () =>
               dispatchSnackbarNegativeEvent(appointmentsTranslationKeys.unsuccessfulUpdatedAppointment),
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(updateAppointment)}>
         <AppointmentAdvancePaymentShowModalEventInterceptor appointmentId={appointmentId}>
            {props.children}
         </AppointmentAdvancePaymentShowModalEventInterceptor>
      </ActionEventInterceptor>
   );
};
